import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocationShare } from "@/context/LocationContext";
import { useNotifications } from "@/context/NotificationsContext";
import { PageHeader, Card, Btn } from "@/components/primitives";
import { initials } from "@/lib/format";
import { toast } from "sonner";
import {
  Bell, BellRing, Loader2, ShieldCheck, Truck, MapPin, User, Phone, MessageSquareText,
  KeyRound, Radio, FileText, BarChart3, Building2, Users, LogOut, ChevronRight, Check, Trash2, Lock, Eye,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const inputCls = "w-full h-10 px-3 rounded-md border border-curtron-line bg-curtron-surface text-sm outline-none focus:border-curtron-green focus:ring-2 focus:ring-curtron-green/20 transition";
const labelCls = "text-[11px] uppercase tracking-[0.05em] font-medium text-curtron-sub";

const REGIONS = [
  { key: "hq_statesboro", label: "HQ – Statesboro, GA" },
  { key: "se_brunswick", label: "Southeast – Brunswick, GA" },
];

function Row({ icon: Icon, label, value, hint, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="w-full flex items-center gap-3 px-4 sm:px-5 py-3.5 text-left hover:bg-curtron-bg transition-colors">
      <span className="w-8 h-8 rounded-md bg-curtron-green/10 text-curtron-green flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wide text-curtron-muted">{label}</div>
        <div className="text-sm text-curtron-ink truncate">{value}</div>
        {hint && <div className="text-xs text-curtron-muted">{hint}</div>}
      </div>
      <ChevronRight className="w-4 h-4 text-curtron-muted shrink-0" />
    </button>
  );
}

export default function Profile() {
  const { user, refreshUser, logout, isAdmin, effectiveIsAdmin, viewAsDriver, setViewAsDriver } = useAuth();
  const { enabled: locEnabled, setEnabled: setLocEnabled, permission: locPerm, sharing: locSharing, lastPingAt, requestLocation } = useLocationShare();
  const { permission, requestPermission } = useNotifications();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(null); // 'name' | 'van' | 'phone' | 'password'
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [van, setVan] = useState(user?.van_number || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");

  const regionLabel = REGIONS.find((r) => r.key === user?.region)?.label || "No region set";

  const patchMe = async (body, msg) => {
    setSaving(true);
    try {
      await api.patch("/auth/me", body);
      await refreshUser();
      toast.success(msg);
      setEditing(null);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const switchRegion = (key) => { if (key !== user?.region) patchMe({ region: key }, "Region updated"); };

  const [smsSaving, setSmsSaving] = useState(false);
  const toggleSms = async (val) => {
    setSmsSaving(true);
    try {
      await api.patch("/auth/me", { sms_opt_in: val });
      await refreshUser();
      toast.success(val ? "SMS dispatch alerts turned on" : "SMS dispatch alerts turned off");
    } catch (e) { toast.error(apiError(e)); } finally { setSmsSaving(false); }
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/users/me");
      toast.success("Your account has been deleted");
      logout();
    } catch (e) { toast.error(apiError(e)); }
  };

  const changePassword = async () => {    if (newPw !== confirmPw) { toast.error("New passwords do not match"); return; }
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.patch("/auth/password", { old_password: oldPw, new_password: newPw });
      toast.success("Password updated");
      setOldPw(""); setNewPw(""); setConfirmPw(""); setEditing(null);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const workspace = [
    { icon: Radio, label: "Fleet", hint: "Live driver map & status", to: "/fleet" },
    { icon: FileText, label: "Documents", hint: "PDFs, run sheets & forms", to: "/documents" },
    { icon: MessageSquareText, label: "Chat", hint: "Team messages & announcements", to: "/chat" },
    ...(effectiveIsAdmin ? [
      { icon: BarChart3, label: "Reports", hint: "Monthly summaries & CSV", to: "/reports" },
      { icon: Building2, label: "Agencies", hint: "Funeral home directory", to: "/agencies" },
      { icon: Users, label: "Manage Users", hint: "Drivers, roles & regions", to: "/users" },
    ] : []),
  ];

  return (
    <div className="max-w-2xl">
      <PageHeader title="Profile & Settings" subtitle="Manage your account and preferences." />

      <Card className="p-5 flex items-center gap-4 mb-4">
        {user?.avatar ? <img src={user.avatar} alt="" className="w-16 h-16 rounded-full object-cover" /> : <div className="w-16 h-16 rounded-full bg-curtron-moss/25 text-curtron-green flex items-center justify-center text-xl font-semibold">{initials(user?.name)}</div>}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading font-semibold text-curtron-ink text-lg">{user?.name}</div>
            <span className="inline-flex items-center gap-1 text-[11px] uppercase bg-curtron-green/12 text-curtron-green rounded-md px-2 py-0.5"><ShieldCheck className="w-3 h-3" /> {user?.role}</span>
          </div>
          <div className="text-sm text-curtron-sub">{user?.email}</div>
          <div className="text-xs text-curtron-muted mt-0.5 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {regionLabel}</div>
        </div>
      </Card>

      {/* Admin: View as Driver */}
      {isAdmin && (
        <Card className="p-4 sm:p-5 mb-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="font-heading font-semibold text-curtron-ink flex items-center gap-2"><Eye className="w-4 h-4 text-curtron-ochre" /> View as Driver</h2>
              <p className="text-sm text-curtron-sub mt-0.5">Preview the app exactly as an assigned driver sees it. Your permissions don't change.</p>
            </div>
            <Switch checked={viewAsDriver} onCheckedChange={setViewAsDriver} data-testid="view-as-driver-switch" />
          </div>
        </Card>
      )}

      {/* Region */}
      <Card className="mb-4">
        <div className="px-4 sm:px-5 py-3 border-b border-curtron-line"><h2 className="text-[11px] uppercase tracking-wide font-medium text-curtron-muted">Region</h2></div>
        <div className="divide-y divide-curtron-line">
          {REGIONS.map((r) => {
            const active = user?.region === r.key;
            return (
              <button key={r.key} onClick={() => switchRegion(r.key)} disabled={saving} data-testid={`region-${r.key}`}
                className="w-full flex items-center gap-3 px-4 sm:px-5 py-3.5 text-left hover:bg-curtron-bg transition-colors disabled:opacity-60">
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${active ? "border-curtron-green bg-curtron-green" : "border-curtron-line"}`}>
                  {active && <Check className="w-3 h-3 text-white" />}
                </span>
                <div className="flex-1"><div className="text-sm text-curtron-ink">{r.label}</div><div className="text-xs text-curtron-muted">{active ? "Currently active" : "Tap to switch"}</div></div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Account fields */}
      <Card className="mb-4 divide-y divide-curtron-line">
        {editing === "name" ? (
          <div className="p-4 sm:p-5 space-y-2">
            <label className={labelCls}>Display Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="profile-name-input" />
            <div className="flex gap-2 justify-end"><Btn variant="secondary" onClick={() => setEditing(null)}>Cancel</Btn><Btn onClick={() => patchMe({ name: name.trim() }, "Name updated")} disabled={saving} data-testid="profile-name-save">Save</Btn></div>
          </div>
        ) : <Row icon={User} label="Display Name" value={user?.name || "—"} onClick={() => { setName(user?.name || ""); setEditing("name"); }} testid="profile-name-row" />}

        {editing === "van" ? (
          <div className="p-4 sm:p-5 space-y-2">
            <label className={labelCls}>Van / Vehicle Number</label>
            <input value={van} onChange={(e) => setVan(e.target.value)} placeholder="e.g. C19" className={inputCls} data-testid="profile-van-input" />
            <div className="flex gap-2 justify-end"><Btn variant="secondary" onClick={() => setEditing(null)}>Cancel</Btn><Btn onClick={() => patchMe({ van_number: van.trim() }, "Van number updated")} disabled={saving} data-testid="profile-van-save">Save</Btn></div>
          </div>
        ) : <Row icon={Truck} label="Van / Vehicle Number" value={user?.van_number || "No van assigned"} hint={!user?.van_number ? "Tap to add your vehicle number" : null} onClick={() => { setVan(user?.van_number || ""); setEditing("van"); }} testid="profile-van-row" />}

        {editing === "phone" ? (
          <div className="p-4 sm:p-5 space-y-2">
            <label className={labelCls}>Phone Number</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 912 555 0000" className={inputCls} data-testid="profile-phone-input" />
            <div className="flex gap-2 justify-end"><Btn variant="secondary" onClick={() => setEditing(null)}>Cancel</Btn><Btn onClick={() => patchMe({ phone: phone.trim() }, "Phone updated")} disabled={saving} data-testid="profile-phone-save">Save</Btn></div>
          </div>
        ) : <Row icon={Phone} label="Phone Number" value={user?.phone || "No phone number on file"} hint={!user?.phone ? "Add one to receive dispatch alerts" : null} onClick={() => { setPhone(user?.phone || ""); setEditing("phone"); }} testid="profile-phone-row" />}
      </Card>

      {/* Location sharing */}
      <Card className="p-4 sm:p-5 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-heading font-semibold text-curtron-ink flex items-center gap-2"><MapPin className="w-4 h-4 text-curtron-info" /> Share Location</h2>
            <p className="text-sm text-curtron-sub mt-0.5">Let dispatch see your live position on the fleet map while the app is open.</p>
          </div>
          <Switch checked={locEnabled} onCheckedChange={setLocEnabled} data-testid="share-location-switch" />
        </div>
        {locEnabled && locPerm === "prompt" && (
          <Btn variant="secondary" className="mt-3" onClick={requestLocation} data-testid="location-enable-btn"><MapPin className="w-4 h-4" /> Enable location access</Btn>
        )}
        {locEnabled && locPerm === "denied" && (
          <div className="mt-3 flex gap-2.5 rounded-md border border-curtron-terracotta/30 bg-curtron-terracotta/8 px-3.5 py-3" data-testid="location-blocked">
            <MapPin className="w-4 h-4 text-curtron-terracotta shrink-0 mt-0.5" />
            <p className="text-sm text-curtron-terracotta">Location is blocked in your browser settings. Allow location for this site, then reload.</p>
          </div>
        )}
        {locEnabled && locPerm === "unsupported" && (
          <p className="mt-2 text-xs text-curtron-muted">This browser doesn't support location sharing.</p>
        )}
        {locEnabled && locSharing && (
          <div className="mt-2 text-xs text-curtron-green inline-flex items-center gap-1" data-testid="location-sharing-active">
            <Check className="w-3.5 h-3.5" /> Sharing live{lastPingAt ? ` · last update ${lastPingAt.toLocaleTimeString()}` : ""}
          </div>
        )}
      </Card>

      {/* SMS */}
      <Card className="p-4 sm:p-5 mb-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-heading font-semibold text-curtron-ink flex items-center gap-2"><MessageSquareText className="w-4 h-4 text-curtron-green" /> SMS Dispatch Alerts</h2>
          {!user?.phone ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase font-semibold bg-curtron-muted/15 text-curtron-muted rounded-full px-2.5 py-1" data-testid="sms-locked-pill">
              <Lock className="w-3 h-3" /> Off
            </span>
          ) : (
            <div className="flex items-center gap-2">
              {smsSaving && <Loader2 className="w-4 h-4 animate-spin text-curtron-muted" />}
              <Switch checked={!!user?.sms_opt_in} disabled={smsSaving} onCheckedChange={toggleSms} data-testid="sms-optin-switch" />
            </div>
          )}
        </div>

        {!user?.phone ? (
          <div className="mt-3 flex gap-2.5 rounded-md border border-curtron-info/30 bg-curtron-info/8 px-3.5 py-3" data-testid="sms-phone-nudge">
            <Phone className="w-4 h-4 text-curtron-info shrink-0 mt-0.5" />
            <p className="text-sm text-curtron-info">Add a phone number above to enable SMS dispatch alerts.</p>
          </div>
        ) : user?.sms_opt_in && user?.sms_opt_in_at ? (
          <div className="mt-2 text-xs text-curtron-green inline-flex items-center gap-1" data-testid="sms-optin-date">
            <Check className="w-3.5 h-3.5" /> Opted in {new Date(user.sms_opt_in_at).toLocaleDateString()}
          </div>
        ) : null}

        <p className="text-xs text-curtron-sub leading-relaxed mt-3">
          Dispatch alerts are sent when a transport call is assigned to you. Msg frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. Your mobile information will not be sold or shared with third parties for promotional or marketing purposes.
        </p>
      </Card>

      {/* Desktop notifications */}
      <Card className="p-4 sm:p-5 mb-4">
        <h2 className="font-heading font-semibold text-curtron-ink mb-1 flex items-center gap-2"><Bell className="w-4 h-4 text-curtron-green" /> Browser Notifications</h2>
        <p className="text-sm text-curtron-sub mb-3">Get clickable alerts for new dispatch calls, messages and announcements — tap one to jump straight to it.</p>
        {permission === "granted" ? (
          <div className="inline-flex items-center gap-2 text-sm text-curtron-green"><BellRing className="w-4 h-4" /> Notifications enabled</div>
        ) : permission === "denied" ? (
          <div className="text-sm text-curtron-terracotta">Blocked in browser settings. Enable notifications for this site to receive alerts.</div>
        ) : (
          <Btn onClick={requestPermission} data-testid="profile-enable-notifications"><Bell className="w-4 h-4" /> Enable notifications</Btn>
        )}
      </Card>

      {/* Security */}
      <Card className="mb-4">
        <div className="px-4 sm:px-5 py-3 border-b border-curtron-line"><h2 className="text-[11px] uppercase tracking-wide font-medium text-curtron-muted">Security</h2></div>
        {editing === "password" ? (
          <div className="p-4 sm:p-5 space-y-3">
            <div><label className={labelCls}>Current Password</label><input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} data-testid="old-password" className={`${inputCls} mt-1`} /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className={labelCls}>New Password</label><input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password" className={`${inputCls} mt-1`} /></div>
              <div><label className={labelCls}>Confirm New</label><input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="confirm-password" className={`${inputCls} mt-1`} /></div>
            </div>
            <div className="flex gap-2 justify-end"><Btn variant="secondary" onClick={() => setEditing(null)}>Cancel</Btn><Btn onClick={changePassword} disabled={saving} data-testid="change-password-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Update Password</Btn></div>
          </div>
        ) : <Row icon={KeyRound} label="Password" value="Change password" onClick={() => setEditing("password")} testid="profile-password-row" />}
      </Card>

      {/* Workspace */}
      <Card className="mb-4">
        <div className="px-4 sm:px-5 py-3 border-b border-curtron-line"><h2 className="text-[11px] uppercase tracking-wide font-medium text-curtron-muted">Workspace</h2></div>
        <div className="divide-y divide-curtron-line">
          {workspace.map((w) => <Row key={w.to} icon={w.icon} label={w.label} value={w.hint} onClick={() => navigate(w.to)} testid={`workspace-${w.label.toLowerCase().replace(/\s+/g, "-")}`} />)}
        </div>
      </Card>

      <Btn variant="secondary" onClick={logout} data-testid="profile-signout" className="w-full text-curtron-terracotta"><LogOut className="w-4 h-4" /> Sign out</Btn>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button className="w-full text-center text-sm text-curtron-terracotta mt-4 py-2 inline-flex items-center justify-center gap-1.5" data-testid="delete-account-btn">
            <Trash2 className="w-4 h-4" /> Delete my account
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes your account and personal SMS logs, and removes your name from chats and transports. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-account-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteAccount} data-testid="delete-account-confirm" className="bg-curtron-terracotta hover:bg-[#A35549]">Delete account</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
